import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Phone, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/phoneFormatter';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const { login, isAuthenticated, error, clearError } = useAuth();

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
    setLocalError('');
  }, [clearError]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'username') {
      // Only allow English letters and numbers for username
      const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: filteredValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    // Basic validation
    if (!formData.username.trim()) {
      setLocalError('Kullanıcı adı gereklidir');
      return;
    }

    if (!formData.password) {
      setLocalError('Şifre gereklidir');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(formData);
      if (!result.success) {
        setLocalError(result.error);
      }
    } catch (err) {
      setLocalError('Beklenmeyen bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setPhoneError('');

    if (!forgotPasswordPhone.trim()) {
      return;
    }

    // Clean phone number (remove spaces and non-digits)
    const cleanPhone = cleanPhoneNumber(forgotPasswordPhone);

    // Check if phone has 11 digits
    if (cleanPhone.length !== 11) {
      setPhoneError('Telefon numarası 11 haneli olmalıdır (0532 555 55 55)');
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: cleanPhone
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Geçici şifre gönderilemedi');
      }

      setForgotPasswordSuccess(true);
    } catch (err) {
      setPhoneError(err.message || 'Geçici şifre gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPasswordDialog = () => {
    setShowForgotPassword(false);
    setForgotPasswordPhone('');
    setForgotPasswordSuccess(false);
    setForgotPasswordLoading(false);
    setPhoneError('');
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setForgotPasswordPhone(formattedPhone);
    if (phoneError) {
      setPhoneError('');
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Veribil Logo" className="h-24 w-auto" />
          </div>
        </div>

        {/* Error message */}
        {displayError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-fade-in">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-600">{displayError}</p>
            </div>
          </div>
        )}

        {/* Login form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Username field */}
            <div>
              <label htmlFor="username" className="form-label">
                Kullanıcı Adı
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                className="form-input placeholder-gray-500"
                placeholder="Kullanıcı adınızı girin"
                value={formData.username}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="form-label">
                Şifre
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="form-input pr-10 placeholder-gray-500"
                  placeholder="Şifrenizi girin"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative px-4 py-3 text-base font-medium bg-[#7a2c2c] hover:bg-[#5a1f1f] text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#7a2c2c] focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Giriş yapılıyor...
                </div>
              ) : (
                'Giriş Yap'
              )}
            </button>
          </div>

          {/* Forgot password link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-[#7a2c2c] hover:text-[#5a1f1f] font-medium"
            >
              Şifremi Unuttum
            </button>
          </div>
        </form>

        {/* Kurumsal bağ: Veribil Vale, veribil.com.tr hizmetidir */}
        <div className="text-center text-xs text-gray-500 pt-2">
          <span className="font-medium text-gray-600">Veribil Vale</span> — bir{' '}
          <a
            href="https://veribil.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#7a2c2c] hover:text-[#5a1f1f] font-medium underline"
          >
            veribil.com.tr
          </a>{' '}
          hizmetidir
        </div>

        {/* Forgot Password Dialog */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Şifre Sıfırlama</h3>
                  <button
                    onClick={closeForgotPasswordDialog}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                {!forgotPasswordSuccess ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Cep telefonu numaranızı girin, size WhatsApp ile geçici bir şifre gönderelim.
                    </p>
                    <form onSubmit={handleForgotPassword}>
                      <div className="mb-4">
                        <label htmlFor="forgot-phone" className="form-label">
                          Cep Telefonu
                        </label>
                        <div className="relative">
                          <input
                            id="forgot-phone"
                            type="tel"
                            className={`form-input pl-10 ${phoneError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="0532 555 55 55"
                            value={forgotPasswordPhone}
                            onChange={handlePhoneChange}
                            required
                            disabled={forgotPasswordLoading}
                            maxLength="14"
                          />
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        </div>
                        {phoneError && (
                          <div className="flex items-center mt-2">
                            <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                            <p className="text-sm text-red-600">{phoneError}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={closeForgotPasswordDialog}
                          className="flex-1 px-4 py-2 h-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center"
                          disabled={forgotPasswordLoading}
                        >
                          İptal
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-4 py-2 h-10 text-sm font-medium text-white bg-[#7a2c2c] border border-transparent rounded-md hover:bg-[#5a1f1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a2c2c] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          disabled={forgotPasswordLoading || !forgotPasswordPhone.trim()}
                        >
                          {forgotPasswordLoading ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-2" />
                              Gönderiliyor...
                            </>
                          ) : (
                            'Geçici Şifre Gönder'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Phone className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Geçici Şifre Gönderildi</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      {forgotPasswordPhone} numarasına WhatsApp ile geçici şifreniz gönderildi.
                      Bu şifreyle giriş yapıp en kısa sürede şifrenizi güncelleyin.
                    </p>
                    <button
                      onClick={closeForgotPasswordDialog}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-[#7a2c2c] hover:bg-[#5a1f1f] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#7a2c2c] focus:ring-offset-2 shadow-md hover:shadow-lg"
                    >
                      Tamam
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;